import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { AgentAvatar } from './AgentAvatar';
import { openAgentChat } from '../utils/agents';

const API = getApiUrl();

/** Lettres du matin : chaque expert laisse 2 remarques sur son module (calculées à 7h, relues à la demande). */
export const ExpertLettersWidget = ({ tenantSlug, onRead }) => {
  const navigate = useNavigate();
  const slug = tenantSlug || window.location.pathname.split('/')[1];
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = (force = false) => axios.get(`${API}/ai/experts/letters`, { params: force ? { force: true } : {}, withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ letters: [] }));
  useEffect(() => { load(); }, []);
  const markRead = () => { if (data?.unread) { axios.post(`${API}/ai/experts/letters/read`, {}, { withCredentials: true }).then(() => { setData((d) => ({ ...d, unread: false })); onRead?.(); }).catch(() => {}); } };
  if (!data) return <div className="card-ios p-5 min-h-[196px] animate-pulse" data-testid="expert-letters-loading" />;
  const letters = data.letters || [];
  const urgent = letters.filter((l) => l.urgent).length;
  return (
    <div className="card-ios p-4 sm:p-5" data-testid="expert-letters-widget" onClick={markRead}>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-[14px] flex items-center justify-center shrink-0 relative" style={{ background: 'var(--brand-soft)', color: 'var(--texte)' }}>
          <Mail size={20} strokeWidth={2.2} />
          {data.unread && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[var(--p-d9442b)] ring-2 ring-white live-dot" data-testid="expert-letters-unread" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[var(--texte-2)]">Lettres du matin · {letters.length} experts</p>
          <p className="text-[14px] text-[var(--texte)] leading-snug">{urgent ? `${urgent} expert${urgent > 1 ? 's ont' : ' a'} quelque chose d'urgent à vous dire.` : 'Rien d\'urgent : vos experts veillent.'}</p>
        </div>
        <button type="button" onClick={(e) => { e.stopPropagation(); load(true); }} className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--texte-2)] hover:bg-[var(--remplissage)]" aria-label="Relire les données" data-testid="expert-letters-refresh"><RefreshCw size={16} /></button>
      </div>
      <div className="space-y-2">
        {letters.map((l) => {
          const isOpen = open === l.agent_key || (open === null && l.urgent && letters.findIndex((x) => x.urgent) === letters.indexOf(l));
          return (
            <div key={l.agent_key} className="rounded-[18px] overflow-hidden" style={{ background: l.urgent ? '#FFF7F2' : '#F5F5F7' }} data-testid={`expert-letter-${l.agent_key}`}>
              <div className="w-full flex items-center gap-3 px-3.5 py-2.5 min-h-[56px]">
                <AgentAvatar agent={l.agent_key} size={40} ring={l.urgent ? 'var(--brand)' : '#E5E5EA'} urgent={l.urgent} testid={`expert-letter-avatar-${l.agent_key}`} />
                <button type="button" onClick={() => setOpen(isOpen ? '' : l.agent_key)} className="flex-1 min-w-0 min-h-[44px] text-left" aria-expanded={isOpen} data-testid={`expert-letter-toggle-${l.agent_key}`}>
                  <span className="block text-[15px] font-semibold text-[var(--texte)]">{l.agent.name} <span className="font-normal text-[var(--texte-2)]">· {l.agent.role}</span></span>
                  {!isOpen && <span className="block text-[13px] text-[var(--texte)] truncate">{l.remarks[0]?.text}</span>}
                </button>
                <ArrowRight size={16} className={`text-[var(--texte-2)] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </div>
              {isOpen && (
                <div className="px-3.5 pb-3.5 space-y-2">
                  {l.remarks.map((r, i) => (
                    <div key={i} className="rounded-[14px] bg-[var(--carte)] px-3.5 py-3">
                      <p className="text-[14px] text-[var(--encre)] leading-snug">{r.urgent && <span className="inline-block w-2 h-2 rounded-full bg-[var(--p-d9442b)] mr-1.5 align-middle" />}{r.text}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {r.action_label && r.action_path && <button type="button" onClick={() => navigate(`/${slug}${r.action_path}`)} className="chip-off !text-[13px] !py-1.5 !px-3 min-h-[36px]" data-testid={`expert-letter-action-${l.agent_key}-${i}`}>{r.action_label} →</button>}
                        <button type="button" onClick={() => openAgentChat(l.agent_key, `À propos de votre remarque du matin : « ${r.text} » — on fait quoi concrètement ?`)} className="text-[13px] font-semibold min-h-[44px] px-3 rounded-[14px]" style={{ color: 'var(--brand-pressed)' }} title={`Approfondir avec ${l.agent.name}`} data-testid={`expert-letter-ask-${l.agent_key}-${i}`}>Approfondir ce conseil</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExpertLettersWidget;
